import React, { useMemo, useCallback, useState, Dispatch, SetStateAction } from 'react'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import useT from '../../../i18ns/admin/useT'
import useGDK from '../../../providers/admin/gdk/useGDK'
import { useGetDataByPayload, useGDKFuncHandleClick, useDialogHandleClick, useReload } from '../../../utils/default/ComplexFlowHook'
import { createGlobalDialogConfig } from '../../../utils/default/DialogHelper'
import useGlobalDialog from '../../../providers/admin/dialog/useGlobalDialog'
import { useChecker } from '../../../utils/admin/AdminRouteHook'
import { PermissionType, PlayerRelationsRes } from '@golden/gdk-admin'
import allRoute from '../route/route'
import { lightFormat } from 'date-fns'

const useStyles = makeStyles((theme) => ({
  root: {
    overflowY: 'scroll',
    maxHeight: '240px',
    padding: 0,
    '&.$opened': {
      maxHeight: '1560px'
    },
    background: 'linear-gradient(to bottom, white 1%, rgba(255,255,255,0)), linear-gradient(to top, rgba(255,255,255,0), white 99%) 0 100%, radial-gradient(farthest-side at 50% 100%, rgba(0,0,0,.2), rgba(0,0,0,0)) 50% 100%, radial-gradient(farthest-side at 50% 0%, rgba(0,0,0,.2), rgba(0,0,0,0)) 50% 0%',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'top, bottom, bottom, top',
    backgroundSize: '100% 100px, 100% 100px, 100% 14px, 100% 14px',
    backgroundAttachment: 'local, local, scroll, scroll'
  },
  opened: {
    maxHeight: '1560px'
  },
  item: {
    alignItems: 'stretch',
    minHeight: 60,
    padding: 0,
    border: '1px solid #e9e9e9',
    '&:not(:first-child)': {
      marginTop: '-1px'
    }
  },
  text: {
    margin: 0,
    padding: 12,
    wordBreak: 'break-word'
  },
  accounts: {
    padding: 12,
    width: 200
  },
  reason: {
    padding: 12,
    borderLeft: '1px solid #e9e9e9',
    flex: 1
  },
  buttons: {
    opacity: 0,
    transition: '0.3s',
    background: 'white',
    transform: 'translate(-50%, -50%)',
    left: '50%',
    top: '50%'
  },
  show: {
    opacity: 1
  },
  button: {
    margin: '0 2px'
  },
  time: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 200,
    borderLeft: '1px solid #e9e9e9'
  },
  action: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 150,
    borderLeft: '1px solid #e9e9e9',
    padding: 12,
    textAlign: 'center',
    '&:hover $buttons': {
      opacity: 1
    }
  }
}))

const PlayerRelationsAddItem: React.FC<{ userId: number, editId: null | number, reload: () => void }> = (payload) => {
  const classes = useStyles()
  const { t } = useT()
  const gdk = useGDK()
  const globalDialog = useGlobalDialog()
  const writable = useChecker([PermissionType.PLAYER_MANAGEMENT_INFO_RELATION])

  const { userId, editId, reload } = payload
  const [inputAccount, setInputAccount] = useState('')
  const [inputReason, setInputReason] = useState('')
  const disabled = useMemo(() => !writable || editId !== null, [writable, editId])

  const { handleClick: handleSave, loading } = useGDKFuncHandleClick({
    payload: { account: inputAccount, reason: inputReason },
    gdkFunc: (payload) => gdk.player.addPlayerRelations(payload, userId),
    gdkFuncDependencies: [gdk, inputAccount],
    onSuccess: () => {
      reload()
      setInputAccount('')
      setInputReason('')
    },
    onError: (error) => {
      globalDialog.setConfig(createGlobalDialogConfig({
        variant: 'error',
        showCancel: false,
        message: error.message
      }))
      globalDialog.setOpen({ id: 'editRelationsFailed', value: true, isOK: false })
    }
  })

  return (
    <ListItem className={classes.item}>
      <Box className={classes.accounts}>
        <TextField
          value={inputAccount}
          fullWidth
          disabled={disabled}
          placeholder={t('placeholder.inputPlayerRelations')}
          onChange={(event) => {
            const value = event.target.value
            setInputAccount(value)
          }}
        />
      </Box>
      <Box className={classes.reason}>
        <TextField
          value={inputReason}
          fullWidth
          disabled={disabled}
          placeholder={t('common.releationsReason')}
          onChange={(event) => {
            const value = event.target.value
            setInputReason(value)
          }}
        />
      </Box>
      <Box position="relative" className={clsx(classes.action)}>
        <Box className={classes.button}>
          <Button
            fullWidth
            size='small'
            variant="outlined"
            color="secondary"
            type="button"
            disableElevation
            disabled={disabled || loading || inputReason === '' || inputAccount === ''}
            onClick={handleSave}
          >
            { t('common.save')}
          </Button>
        </Box>
      </Box>
    </ListItem>
  )
}

const PlayerRelationsItem: React.FC<PlayerRelationsRes<Date> & { userId: number, editId: null | number, setEditId: Dispatch<SetStateAction<number | null>>, reload: () => void }> = (payload) => {
  const classes = useStyles()
  const { t } = useT()
  const gdk = useGDK()
  const globalDialog = useGlobalDialog()
  const writable = useChecker([PermissionType.PLAYER_MANAGEMENT_INFO_RELATION])

  const { userId, id, updated_at: updatedAt, last_bet_at: lastBetAt, related_user: user, reason, admin, editId, setEditId, reload } = payload
  const initInputAccounts = useCallback(() => user.account, [user])
  const initInputReason = useCallback(() => reason, [reason])

  const [inputAccount, setInputAccount] = useState(initInputAccounts())
  const [inputReason, setInputReason] = useState(initInputReason())

  const content = useMemo(() => {
    return (
      <Box>
        {`[${lightFormat(updatedAt, 'yyyy-MM-dd')}]`} {<Link target="_blank" to={allRoute.playerDetail.encodePath({ search: {}, param: { id: user.id } })}>
          {user.account}
        </Link>} {reason}
      </Box>
    )
  }, [payload])
  const isEdit = useMemo(() => editId === id, [editId, id])

  const handleEdit = useCallback(() => {
    setInputAccount(initInputAccounts())
    setInputReason(initInputReason())
    setEditId(isEdit ? null : id)
  }, [id, isEdit, initInputReason, initInputAccounts])
  const { handleClick: handleSaveEdit, loading } = useGDKFuncHandleClick({
    payload: { account: inputAccount, reason: inputReason },
    gdkFunc: (payload) => gdk.player.editPlayerRelations(payload, { user: userId, relation: id }),
    gdkFuncDependencies: [gdk, inputAccount, inputReason],
    onSuccess: () => {
      reload()
      setEditId(null)
    },
    onError: (error) => {
      globalDialog.setConfig(createGlobalDialogConfig({
        variant: 'error',
        showCancel: false,
        message: error.message
      }))
      globalDialog.setOpen({ id: 'editRelationsFailed', value: true, isOK: false })
    }
  })

  const handleDelete = useDialogHandleClick({
    dialogId: `deletePlayerRelations-${id}`,
    globalDialog,
    changeDialogConfig: createGlobalDialogConfig({
      showIcon: false,
      message: t('dialog.deletePlayerRelations')
    }),
    successDialogConfig: createGlobalDialogConfig({
      showIcon: false,
      message: t('dialog.deleteFinish'),
      showCancel: false
    }),
    getFailDialogConfig: (error: string) => createGlobalDialogConfig({
      variant: 'error',
      showIcon: true,
      message: error,
      showCancel: false
    }),
    payload: { user: userId, relation: id },
    gdkFunc: (payload) => gdk.player.deletePlayerRelations(payload),
    gdkFuncDependencies: [gdk, userId, id],
    onSuccess: () => {
      reload()
      setEditId(null)
    },
    onError: (error) => {
      globalDialog.setConfig(createGlobalDialogConfig({
        variant: 'error',
        showCancel: false,
        message: error.message
      }))
      globalDialog.setOpen({ id: 'editRelationsFailed', value: true, isOK: false })
    }
  })

  return (
    <ListItem className={classes.item}>
      {
        isEdit
          ? (
          <>
            <Box className={classes.accounts}>
              <TextField
                value={inputAccount}
                fullWidth
                onChange={(event) => {
                  const value = event.target.value
                  setInputAccount(value)
                }}
              />
            </Box>
            <Box className={classes.reason}>
              <TextField
                value={inputReason}
                fullWidth
                onChange={(event) => {
                  const value = event.target.value
                  setInputReason(value)
                }}
              />
            </Box>
          </>
            )
          : (
          <ListItemText className={classes.text}>
            {content}
          </ListItemText>
            )
      }
      <Box className={classes.time}>
        {lastBetAt ? lightFormat(lastBetAt, 'yyyy-MM-dd HH:mm:ss') : '--'}
      </Box>
      <Box position="relative" className={classes.action}>
        <Box>{ admin.account }</Box>
        <Box
          position="absolute"
          display='flex' alignItems='center' justifyContent='center'
          className={clsx(classes.buttons, isEdit ? classes.show : '')}
        >
          <Box className={classes.button}>
            <Button
              fullWidth
              size='small'
              variant="outlined"
              color="secondary"
              type="button"
              disableElevation
              disabled={!writable || loading}
              onClick={isEdit ? () => { handleSaveEdit() } : () => { handleEdit() }}
            >
              { isEdit ? t('common.save') : t('common.edit')}
            </Button>
          </Box>
          <Box className={classes.button}>
            <Button
              fullWidth
              size='small'
              variant="outlined"
              color="secondary"
              type="button"
              disableElevation
              disabled={!writable}
              onClick={isEdit ? () => { handleEdit() } : () => { handleDelete() }}
            >
              { isEdit ? t('common.cancel') : t('common.delete')}
            </Button>
          </Box>
        </Box>
      </Box>
    </ListItem>
  )
}

const PlayerRelationsList: React.FC<{ id: number }> = ({ id }) => {
  const classes = useStyles()
  const commonClasses = useCommonStyles()
  const { t } = useT()
  const gdk = useGDK()
  const [opened, setOpened] = useState(false)
  const [list, setList] = useState<Array<PlayerRelationsRes<Date>>>([])
  const [editID, setEditId] = useState<null | number>(null)
  const { reload, reloadFlag } = useReload()

  useGetDataByPayload({
    payload: id,
    gdkFunc: (payload) => gdk.player.getPlayerRelations(payload),
    gdkFuncDependencies: [gdk, reloadFlag],
    onSuccess: (res: { data: Array<PlayerRelationsRes<Date>> }) => {
      setList(res.data)
    }
  })

  return (
    <>
      <Box display='flex' paddingY={2}>
        <Box>{t('common.relationsPlayer')}({list.length})</Box>
        <Box className={commonClasses.anchor} marginX={2} onClick={() => { setOpened(!opened) }}>{t('common.open2')}/{t('common.close2')}</Box>
      </Box>
      <List className={clsx(classes.root, { [classes.opened]: opened })}>
        <PlayerRelationsAddItem userId={id} editId={editID} reload={reload} />
        <ListItem className={clsx(classes.item, commonClasses.greyTableRow)}>
          {
            list.length > 0 ? (
              <>
                <ListItemText className={classes.text}>
                  {t('common.releationsReason')}
                </ListItemText>
                <Box className={classes.time}>
                  {t('common.playerBetLastAt')}
                </Box>
                <Box position="relative" className={classes.action}>
                  {t('common.updateBy')}
                </Box>
              </>
            ) : (
                <Box className={classes.text}>{t('common.noData')}</Box>
            )
          }
        </ListItem>
        {list.map((data) => (
          <PlayerRelationsItem key={data.id} {...data} userId={id} editId={editID} setEditId={setEditId} reload={reload} />
        ))}
      </List>
    </>
  )
}

export default React.memo(PlayerRelationsList)
